/**
 * Timezone helpers. The platform stores all event timestamps in UTC and
 * carries an IANA `timezone` string per event. Forms collect dates with
 * `<input type="datetime-local">` which deals in *naïve* wall-clock strings —
 * this module bridges that naïve format and UTC instants while honouring the
 * event's timezone. Without these helpers a user picking 18:00 in
 * Europe/Ljubljana would silently be saved as 18:00 UTC and re-display as
 * 20:00 (in summer) on the read view.
 *
 * Implementation note: there is no `Date` constructor in JS that takes a
 * timezone, so we compute the offset by formatting a UTC-guess instant in the
 * target timezone and measuring the difference. Works for any IANA zone the
 * runtime knows about (Node 22 + modern browsers ship full tzdata).
 */

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Parse a naïve `YYYY-MM-DDTHH:mm[:ss]` string + IANA timezone into the UTC
 * instant that represents that wall-clock time IN that timezone.
 */
export function localStringToUtc(localStr: string, timezone: string): Date {
  const m = LOCAL_RE.exec(localStr);
  if (!m) {
    const fallback = new Date(localStr);
    if (Number.isFinite(fallback.getTime())) return fallback;
    throw new Error(`Invalid datetime-local string: ${localStr}`);
  }
  const [, ys, mos, ds, hs, mis, ss] = m;
  const y = Number(ys);
  const mo = Number(mos);
  const d = Number(ds);
  const h = Number(hs);
  const mi = Number(mis);
  const s = ss ? Number(ss) : 0;

  // Step 1: take a guess — assume the wall-clock time is UTC. This produces
  // an instant whose UTC representation has the same Y/M/D/H/m/s as the input.
  const guessMs = Date.UTC(y, mo - 1, d, h, mi, s);
  // Step 2: ask "if `guess` is the correct UTC instant, what wall-clock time
  // does it correspond to in `timezone`?" — that gives us the timezone's
  // offset at that moment.
  const wallclock = utcToWallclockMs(guessMs, timezone);
  const offset = wallclock - guessMs; // positive east of UTC
  return new Date(guessMs - offset);
}

/**
 * Format a UTC instant as a `YYYY-MM-DDTHH:mm` wall-clock string in the given
 * IANA timezone. Suitable for hydrating a `<input type="datetime-local">`.
 */
export function utcToLocalString(utc: Date | string, timezone: string): string {
  const d = typeof utc === 'string' ? new Date(utc) : utc;
  if (!Number.isFinite(d.getTime())) return '';
  const parts = formatParts(d, timezone);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * Detect the runtime's current IANA timezone (e.g. for default form values).
 */
export function detectTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * The full list of IANA timezones the runtime supports — used for the
 * timezone picker. Falls back to a conservative shortlist on older runtimes.
 */
export function listTimezones(): readonly string[] {
  type IntlExt = { supportedValuesOf?: (k: 'timeZone') => string[] };
  const intl = Intl as unknown as IntlExt;
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      return intl.supportedValuesOf('timeZone') as readonly string[];
    } catch {
      /* fall through */
    }
  }
  return FALLBACK_TZ_LIST;
}

const FALLBACK_TZ_LIST = [
  'UTC',
  'Europe/London',
  'Europe/Ljubljana',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Belgrade',
  'Europe/Athens',
  'Europe/Moscow',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Tokyo',
  'Australia/Sydney',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
] as const;

// -----------------------------------------------------------------------------
// Internal: parts via Intl.DateTimeFormat
// -----------------------------------------------------------------------------

interface Parts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

function formatParts(d: Date, timezone: string): Parts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  let hour = get('hour');
  if (hour === '24') hour = '00';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
  };
}

function utcToWallclockMs(utcMs: number, timezone: string): number {
  const p = formatParts(new Date(utcMs), timezone);
  return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
}

/**
 * Format a UTC instant for *display* in a given timezone, locale-aware.
 * Mirrors the format strings used elsewhere ("Wed, 30 Apr 2026 · 18:30").
 */
export function formatInTz(
  utc: Date | string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  },
): string {
  const d = typeof utc === 'string' ? new Date(utc) : utc;
  if (!Number.isFinite(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { ...options, timeZone: timezone }).format(d);
}
